import { useMemo } from 'react';

import { useMeQuery } from '@/store/people/people.store';
import {
  useBilledServiceIdsQuery,
  useSubscriptionsQuery,
} from '@/store/subscriptions/subscriptions.store';
import type { Subscription } from '@/store/subscriptions/subscriptions.types';
import { useVesselsByIdsQuery } from '@/store/vessels/vessels.store';
import type { Vessel } from '@/store/vessels/vessels.types';

import { canEditPlan, membershipOf, planManageableServices } from './subscription.access';
import { isVesselOwner } from './subscription.takeover';

export type ManagedVesselSubscription = {
  vessel: Vessel;
  /**
   * `null` when the vessel has no subscription row at all. To the user that reads the
   * same as a `harbor` row: the free plan.
   */
  subscription: Subscription | null;
  /**
   * Whether this caller may change the plan, or only look at it. An owner can withdraw
   * `EditSubscription` from a role, and the rows still have to render for whoever is left
   * holding `GetSubscription` alone.
   */
  canEdit: boolean;
  /**
   * Whether the plan is billed to this caller. A vessel's row says what the plan is and never
   * whose card it is on, so this is read from the caller's own bill instead — see
   * `getBilledServiceIds`. Unknown reads as `true`: that is the ordinary answer, and the one
   * that offers nothing the server would refuse.
   */
  isPayer: boolean;
  /**
   * Whether this caller owns the vessel — the other half of what makes a plan takeable. See
   * `subscription.takeover`.
   */
  isOwner: boolean;
};

/**
 * Every vessel whose plan the signed-in user may manage, each joined to its subscription.
 *
 * Which vessels those are comes from their own role rows — `GET /me` returns one per service
 * with the rights it carries — rather than from ownership. A vessel's plan is not the owner's
 * alone: a captain can be the one paying for a vessel that has no owner yet, and is granted
 * `GetSubscription` / `EditSubscription` for exactly that, so a list filtered by `owner_id`
 * showed a captain nothing and left them no way to buy the plan they are meant to buy. See
 * `subscription.access`.
 *
 * The two halves come from different services: the vessels from the vessel API by id, and
 * each vessel's plan from `GET /subscriptions` scoped to it. Per vessel rather than one
 * `/subscriptions/all` — see `useSubscriptionsQuery` for why that endpoint answers the wrong
 * question here — and the reads are the same ones the detail view makes, so a row opens
 * without a request.
 *
 * The vessel list needs an `X-Service-Id` header, and this page spans every vessel rather
 * than one of them, so the first manageable vessel satisfies it. It has to be a vessel the
 * caller holds a role on rather than merely their first service — the header is what the
 * `GetVesselInfo` gate is read off, and a first service that is a service-provider account
 * carries no vessel rights at all.
 */
export const useManagedVesselSubscriptions = () => {
  const meQuery = useMeQuery();
  const me = meQuery.data;

  const services = useMemo(() => planManageableServices(me), [me]);
  const ids = useMemo(() => services.map((service) => service.serviceId), [services]);
  const serviceId = ids[0];

  const vesselsQuery = useVesselsByIdsQuery({ ids, serviceId });
  const subscriptions = useSubscriptionsQuery(ids);
  const { byServiceId } = subscriptions;
  // One request for the whole list: which of these vessels are on the caller's own card.
  const billed = useBilledServiceIdsQuery(serviceId);

  const items = useMemo<ManagedVesselSubscription[]>(
    () =>
      (vesselsQuery.data?.items ?? []).map((vessel) => ({
        vessel,
        subscription: byServiceId.get(vessel.id) ?? null,
        canEdit: canEditPlan(membershipOf(me, vessel.id)),
        isPayer: billed.data?.has(vessel.id) ?? true,
        isOwner: isVesselOwner(vessel, me),
      })),
    [me, vesselsQuery.data, byServiceId, billed.data],
  );

  // Nothing to show, and nothing to ask for either: without a service the queries stay
  // disabled, would report `isPending` forever, and the list would spin.
  const hasNothingToManage = !!me && !serviceId;

  return {
    items,
    // Vessels drive the rows; subscriptions only decorate them, so the list can render
    // as soon as the vessels land.
    isLoading: !hasNothingToManage && (meQuery.isPending || vesselsQuery.isPending),
    isSubscriptionsPending: !hasNothingToManage && (subscriptions.isPending || billed.isPending),
    /*
     * The bill is deliberately left out of this. It decides which control a vessel is offered,
     * nothing that is drawn, and its fallback — the caller pays — is the ordinary answer;
     * failing the whole list over it would hide every vessel to protect one button.
     */
    error: meQuery.error ?? vesselsQuery.error ?? subscriptions.error,
  };
};
